<template>
    <ConfirmationModal
        additional-class-name="DeleteUserProcedure"
        title="Are you sure you want to delete this user?"
        yes="Delete User"
        yes-color="danger"
        no="Cancel"
        no-color="neutral"
        :progress="submitting"
        @input="onConfirm"
    >
        <div>
            You are about to remove <span class="text-eidTrustGreen font-semibold">{{ user.name }}</span> as a user.<br>
            This action can not be undone.
        </div>
    </ConfirmationModal>
</template>

<script lang="ts">
    import { Component, Prop, Vue } from '@evidentid/vue-property-decorator';

    import delay from '@evidentid/universal-framework/delay';
    import { User } from '@evidentid/user-management-api-client/types';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import ConfirmationModal from '@/components/confirmation-modal/ConfirmationModal.vue';
    import { useUserManagementStore } from '@/modules/user-management/store';
    import { useSnackbarStore } from '@evidentid/dashboard-commons/modules/snackbar/store';

    // PRODUCT-26110 QA fixes4: declares every event this procedure component `$emit`s --
    // without a declared `emits`, Vue 3 warns "Component emitted event 'abort'/'finish'
    // but it is neither declared in the emits option nor as an 'onAbort'/'onFinish' prop"
    // every time it fires.
    @Component({
        components: {
            ConfirmationModal,
        },
        emits: [ 'abort', 'finish' ],
    })
    export default class DeleteUserProcedure extends Vue {
        // PRODUCT-26110 QA fixes4: `uuid` is unused; declared purely so Vue 3 doesn't warn
        // "Extraneous non-props attributes (uuid)" (see UploadEntityDocumentProcedure.vue).
        @Prop({ type: String, default: '' })
        private uuid?: string;

        @Prop({ type: String })
        private rpName!: string;

        @Prop({ type: Object })
        private user!: User;
        // TODO[PRODUCT-24516]: use a proper fix instead of delay
        private delayLoading = false;

        private get status(): { status: OperationStatus, error: any } {
            return useUserManagementStore().deleteUserStatus[this.rpName]?.[this.user.id] || {
                status: OperationStatus.uninitialized,
                error: null,
            };
        }

        private get submitting() {
            return this.status.status === OperationStatus.loading || this.delayLoading;
        }

        private success(message: string): void {
            useSnackbarStore().displaySnackbar({ message, success: true });
        }

        private error(message: string): void {
            useSnackbarStore().displaySnackbar({ message, success: false });
        }

        private abort(): void {
            this.$emit('abort');
        }

        private onConfirm(confirmed: boolean): void {
            if (confirmed) {
                this.deleteUser();
            } else {
                this.abort();
            }
        }

        private async deleteUser(): Promise<void> {
            if (this.status.status === OperationStatus.loading) {
                return;
            }

            await useUserManagementStore().deleteUser({
                rpName: this.rpName,
                id: this.user.id,
            });

            this.delayLoading = true;
            await delay(1000);
            this.delayLoading = false;

            const { status } = this.status;
            if (status === OperationStatus.success) {
                this.success(`${this.user.email} was removed`);
                this.$emit('finish');
            } else {
                this.error('Oops, something went wrong. Please try again!');
            }
        }
    }
</script>
